import { useState } from 'react'
import { TeamPerformanceIcon1 } from '@forwardflow/assets/svg/TeamPerformanceIcon1'
import { useSearchParams } from 'react-router-dom'
import { useGetDashboardChartsTeamPerformance } from '@forwardflow/hooks/customHooks'
import { TimePeriod } from '@forwardflow/types'
import { Skeleton } from 'antd'

function CircleChart({
   percent,
   color,
   label,
   change,
}: {
   percent: number
   color: string
   label: string
   change: string
}) {
   const radius = 100
   const stroke = 15
   const normalizedRadius = radius - stroke / 2
   const circumference = normalizedRadius * 2 * Math.PI
   const strokeDashoffset = circumference - (percent / 100) * circumference

   return (
      <div className="relative flex flex-col items-center justify-center">
         <svg width={220} height={220}>
            <circle
               stroke={color}
               opacity={0.2}
               fill="transparent"
               strokeWidth={stroke}
               r={normalizedRadius}
               cx={110}
               cy={110}
            />
            <circle
               stroke={color}
               fill="transparent"
               strokeWidth={stroke}
               strokeLinecap="round"
               strokeDasharray={circumference + ' ' + circumference}
               strokeDashoffset={strokeDashoffset}
               r={normalizedRadius}
               cx={110}
               cy={110}
               style={{ transition: 'stroke-dashoffset 0.5s' }}
               transform="rotate(-90 110 110)"
            />
         </svg>
         <div className="absolute top-0 left-0 flex flex-col items-center justify-center w-full h-full">
            <span className="text-2xl font-bold text-white">{percent}%</span>
            <span className="text-base text-white">{label}</span>
            <span className="mt-1 text-sm text-[#00AB40]">{change}</span>
         </div>
      </div>
   )
}

function getInitials(name: string) {
   return name
      ?.split(' ')
      ?.map((n) => n[0])
      ?.join('')
      ?.toUpperCase()
}

function getAgentColor(rankCategory: string) {
   if (rankCategory === 'best') return 'bg-[#25D657]/20'
   if (rankCategory === 'worst') return 'bg-[#FF6D99]/20'
   return 'bg-gray-400/20'
}

function getTextColor(rankCategory: string) {
   if (rankCategory === 'best') return 'text-[#25D657]'
   if (rankCategory === 'worst') return 'text-[#FF6D99]'
   return 'text-gray-400'
}

export const TeamPerformancePanel = () => {
   const [activeTab, setActiveTab] = useState<'both' | 'ff'>('both')
   const [searchParams] = useSearchParams()
   const timePeriod = searchParams.get('timePeriod') || 'allTime'
   const {
      data: DashboardChartsOverview,
      isLoading: DashboardChartsOverviewLoading,
   } = useGetDashboardChartsTeamPerformance({
      timeFrame: timePeriod as TimePeriod,
   })

   const tabData = DashboardChartsOverview?.[activeTab]
   const metrics = tabData?.metrics
   const summary = tabData?.summary
   const agentRankings = tabData?.agentRankings || []

   if (DashboardChartsOverviewLoading) {
      return (
         <div className="flex items-center justify-center h-96">
            <Skeleton
               active
               paragraph={{ rows: 10 }}
               title={{ width: '100%' }}
               className="w-full"
            />
         </div>
      )
   }

   return (
      <div className="p-0 overflow-hidden shadow-lg bg-white/5 rounded-2xl">
         <div className="flex flex-wrap items-center h-auto gap-3 px-4 py-3 pb-3 border-b border-white/20 md:px-6 md:py-4 md:pb-4 md:h-14">
            <TeamPerformanceIcon1 />
            <span className="text-base font-medium md:text-lg">
               {DashboardChartsOverview?.[activeTab]?.title || ''}
            </span>
            <div className="flex-1" />
            <div className="flex overflow-hidden rounded bg-white/5">
               <button
                  className={`px-3 py-1 text-xs md:px-4 md:py-1 md:text-sm font-medium ${
                     activeTab === 'both' ? 'bg-white/10' : ''
                  }`}
                  onClick={() => setActiveTab('both')}
               >
                  Both Agents & FF
               </button>
               <button
                  className={`px-3 py-1 text-xs md:px-4 md:py-1 md:text-sm font-medium ${
                     activeTab === 'ff' ? 'bg-white/10' : ''
                  }`}
                  onClick={() => setActiveTab('ff')}
               >
                  Forward Flow
               </button>
            </div>
         </div>
         <div className="flex flex-col gap-6 px-2 py-4 md:gap-0 md:px-6 md:py-6 xl:flex-row">
            <div className="flex flex-col justify-center w-full gap-4 xl:w-3/5">
               <div className="flex flex-col items-center justify-center gap-8 md:flex-row md:gap-12">
                  <CircleChart
                     percent={metrics?.primary?.value ?? 0}
                     color="#9061DA"
                     label={metrics?.primary?.title ?? ''}
                     change={
                        metrics?.primary?.change
                           ? `${
                                metrics.primary.change.isIncrement ? '+' : '-'
                             }${metrics.primary.change.percent}%`
                           : ''
                     }
                  />
                  <CircleChart
                     percent={metrics?.secondary?.value ?? 0}
                     color="#25D657"
                     label={metrics?.secondary?.title ?? ''}
                     change={
                        metrics?.secondary?.change
                           ? `${
                                metrics.secondary.change.isIncrement ? '+' : '-'
                             }${metrics.secondary.change.percent}%`
                           : ''
                     }
                  />
               </div>
               <div className="flex flex-col items-center justify-center gap-4 md:flex-row md:gap-10">
                  <div className="flex flex-col items-center gap-1">
                     <span className="text-lg font-semibold text-[#B98BE6]">
                        {activeTab === 'both'
                           ? summary?.totalCalls ?? '--'
                           : summary?.totalTransfers ?? '--'}
                     </span>
                     <span className="text-sm">
                        {activeTab === 'both'
                           ? ` Total Calls`
                           : `Total Transfers`}
                     </span>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                     <span className="text-lg font-semibold text-[#42C8F5]">
                        {summary?.averageDurationMinutes
                           ? `${summary.averageDurationMinutes} min`
                           : '--'}
                     </span>
                     <span className="text-sm">Average Duration</span>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                     <span className="text-lg font-semibold text-[#33C865]">
                        {summary?.conversions ?? '--'}
                     </span>
                     <span className="text-sm">Conversions</span>
                  </div>
               </div>
            </div>
            <div className="flex flex-col w-full pl-0 mt-8 xl:w-2/5 xl:pl-8 xl:mt-0">
               <div className="flex px-2 mb-2 text-xs text-white/70">
                  <div className="w-10 mr-1">Rank</div>
                  <div className="flex-1">Agent</div>
                  <div>
                     {activeTab === 'both' ? ` Conversion Rate` : `Acceptance`}
                  </div>
               </div>
               <div className="flex flex-col gap-2 overflow-x-auto max-h-80">
                  {agentRankings.map((agent) => (
                     <div
                        key={agent.rank}
                        className="flex items-center px-2 py-2 rounded-lg bg-[#D9D9D9]/10 h-14 min-w-[320px] md:min-w-0"
                     >
                        <div
                           className={`w-10 mr-1 text-lg font-bold text-center ${getTextColor(
                              agent.rankCategory
                           )}`}
                        >
                           {agent.rank}
                        </div>
                        <div className="flex items-center flex-1 gap-2">
                           <span
                              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${getAgentColor(
                                 agent.rankCategory
                              )}`}
                           >
                              {getInitials(agent.name)}
                           </span>
                           <span className="flex-1 text-sm font-medium text-white truncate">
                              {agent.name}
                           </span>
                        </div>
                        <div className="mr-3 text-base font-semibold text-white">
                           {activeTab === 'both'
                              ? ` ${agent.conversionRate ?? '-- '}%`
                              : `${agent.acceptance ?? '-- '}%`}
                        </div>
                     </div>
                  ))}
               </div>
            </div>
         </div>
      </div>
   )
}
